import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { RefType } from '@hyphen-db-studio/schema-core';
import DbmlEditor from '../editor/DbmlEditor';
import DiagramExplanation from '../workspace/DiagramExplanation';
import { parseTourSteps } from '../../lib/tour';
import type { ReferenceHandler } from '../workspace/Markdown';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import { useSchemaStore, type SelectionRef } from '../../store/useSchemaStore';

const REF_TYPES: { value: RefType; label: string }[] = [
  { value: '>', label: 'many-to-one (>)' },
  { value: '<', label: 'one-to-many (<)' },
  { value: '-', label: 'one-to-one (-)' },
  { value: '<>', label: 'many-to-many (<>)' },
];

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  'w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:border-sky-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
const miniInputClass =
  'rounded border border-gray-200 px-1.5 py-1 text-xs focus:border-sky-400 focus:outline-none dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100';
const readonlyBoxClass = 'rounded-md bg-gray-50 px-2 py-1.5 text-sm text-gray-700 dark:bg-gray-800 dark:text-gray-300';
const dangerButtonClass =
  'flex w-full items-center justify-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10';

function TableProperties({ tableId }: { tableId: string }) {
  const table = useSchemaStore((s) => s.schema.tables.find((t) => t.id === tableId));
  const updateTable = useSchemaStore((s) => s.updateTable);
  const deleteTable = useSchemaStore((s) => s.deleteTable);
  const addColumn = useSchemaStore((s) => s.addColumn);
  const updateColumn = useSchemaStore((s) => s.updateColumn);
  const deleteColumn = useSchemaStore((s) => s.deleteColumn);
  const setTableGroup = useSchemaStore((s) => s.setTableGroup);
  const groups = useSchemaStore((s) => s.schema.groups);

  if (!table) return null;

  return (
    <div>
      <Field label="Table name">
        <input className={inputClass} value={table.name} onChange={(e) => updateTable(table.id, { name: e.target.value })} />
      </Field>
      <Field label="Group">
        <select
          className={inputClass}
          value={table.groupId ?? ''}
          onChange={(e) => setTableGroup(table.id, e.target.value || null)}
        >
          <option value="">No group</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Note">
        <textarea
          className={inputClass}
          rows={2}
          value={table.note ?? ''}
          onChange={(e) => updateTable(table.id, { note: e.target.value })}
        />
      </Field>

      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Columns</span>
        <button onClick={() => addColumn(table.id)} className="flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 dark:text-sky-400 dark:hover:text-sky-300">
          <Plus className="h-3 w-3" /> Add
        </button>
      </div>
      <div className="space-y-2">
        {table.columns.map((col) => (
          <div key={col.id} className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
            <div className="mb-1.5 flex gap-1.5">
              <input
                className={`w-1/2 ${miniInputClass}`}
                value={col.name}
                onChange={(e) => updateColumn(table.id, col.id, { name: e.target.value })}
              />
              <input
                className={`w-1/2 ${miniInputClass}`}
                value={col.type}
                onChange={(e) => updateColumn(table.id, col.id, { type: e.target.value })}
              />
              <button onClick={() => deleteColumn(table.id, col.id)} className="shrink-0 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2 text-[11px] text-gray-600 dark:text-gray-400">
              {(['pk', 'unique', 'nullable', 'increment'] as const).map((key) => {
                const isNullable = key === 'nullable';
                const checked = isNullable ? !col.settings.notNull && !col.settings.pk : !!col.settings[key];
                return (
                  <label key={key} className={`flex items-center gap-1 ${isNullable && col.settings.pk ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isNullable && !!col.settings.pk}
                      onChange={(e) =>
                        updateColumn(table.id, col.id, {
                          settings: isNullable ? { notNull: !e.target.checked } : { [key]: e.target.checked },
                        })
                      }
                    />
                    {key}
                  </label>
                );
              })}
            </div>
            <input
              placeholder="default value"
              className={`mt-1.5 w-full ${miniInputClass}`}
              value={col.settings.default ?? ''}
              onChange={(e) => updateColumn(table.id, col.id, { settings: { default: e.target.value } })}
            />
          </div>
        ))}
      </div>

      <button onClick={() => deleteTable(table.id)} className={`mt-4 ${dangerButtonClass}`}>
        <Trash2 className="h-4 w-4" /> Delete table
      </button>
    </div>
  );
}

function GroupProperties({ groupId }: { groupId: string }) {
  const group = useSchemaStore((s) => s.schema.groups.find((g) => g.id === groupId));
  const tables = useSchemaStore((s) => s.schema.tables);
  const updateGroup = useSchemaStore((s) => s.updateGroup);
  const deleteGroup = useSchemaStore((s) => s.deleteGroup);
  const setTableGroup = useSchemaStore((s) => s.setTableGroup);

  if (!group) return null;
  const members = tables.filter((t) => t.groupId === group.id);

  return (
    <div>
      <Field label="Group name">
        <input className={inputClass} value={group.name} onChange={(e) => updateGroup(group.id, { name: e.target.value })} />
      </Field>
      <Field label="Color">
        <input
          type="color"
          className="h-8 w-16 rounded border border-gray-200 dark:border-gray-700"
          value={group.color ?? '#6366f1'}
          onChange={(e) => updateGroup(group.id, { color: e.target.value })}
        />
      </Field>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Member tables</span>
      <div className="mb-4 space-y-1">
        {members.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded border border-gray-100 px-2 py-1 text-sm dark:border-gray-700 dark:text-gray-200">
            {t.name}
            <button onClick={() => setTableGroup(t.id, null)} className="text-xs text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400">
              remove
            </button>
          </div>
        ))}
        {!members.length && (
          <div className="text-xs text-gray-400 dark:text-gray-500">No tables assigned. Set a table's Group field to add it here.</div>
        )}
      </div>
      <button onClick={() => deleteGroup(group.id)} className={dangerButtonClass}>
        <Trash2 className="h-4 w-4" /> Delete group
      </button>
    </div>
  );
}

function RelationshipProperties({ relationshipId }: { relationshipId: string }) {
  const rel = useSchemaStore((s) => s.schema.relationships.find((r) => r.id === relationshipId));
  const tables = useSchemaStore((s) => s.schema.tables);
  const updateRelationship = useSchemaStore((s) => s.updateRelationship);
  const deleteRelationship = useSchemaStore((s) => s.deleteRelationship);

  if (!rel) return null;
  const fromTable = tables.find((t) => t.id === rel.fromTableId);
  const toTable = tables.find((t) => t.id === rel.toTableId);
  const fromColumn = fromTable?.columns.find((c) => c.id === rel.fromColumnId);
  const toColumn = toTable?.columns.find((c) => c.id === rel.toColumnId);

  return (
    <div>
      <Field label="From">
        <div className={readonlyBoxClass}>
          {fromTable?.name}.{fromColumn?.name}
        </div>
      </Field>
      <Field label="To">
        <div className={readonlyBoxClass}>
          {toTable?.name}.{toColumn?.name}
        </div>
      </Field>
      <Field label="Cardinality">
        <select
          className={inputClass}
          value={rel.type}
          onChange={(e) => updateRelationship(rel.id, { type: e.target.value as RefType })}
        >
          {REF_TYPES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </Field>
      <button onClick={() => deleteRelationship(rel.id)} className={`mt-2 ${dangerButtonClass}`}>
        <Trash2 className="h-4 w-4" /> Delete relationship
      </button>
    </div>
  );
}

function EnumProperties({ enumId }: { enumId: string }) {
  const en = useSchemaStore((s) => s.schema.enums.find((e) => e.id === enumId));
  const deleteEnum = useSchemaStore((s) => s.deleteEnum);
  if (!en) return null;
  return (
    <div>
      <Field label="Enum name">
        <div className={readonlyBoxClass}>{en.name}</div>
      </Field>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Values</span>
      <div className="mb-4 space-y-1">
        {en.values.map((v) => (
          <div key={v.id} className="rounded border border-gray-100 px-2 py-1 text-sm dark:border-gray-700 dark:text-gray-200">
            {v.name}
          </div>
        ))}
      </div>
      <button onClick={() => deleteEnum(en.id)} className={dangerButtonClass}>
        <Trash2 className="h-4 w-4" /> Delete enum
      </button>
    </div>
  );
}

function ProjectDocumentation() {
  const documentation = useSchemaStore((s) => s.documentation);
  const setDocumentation = useSchemaStore((s) => s.setDocumentation);
  const stats = useSchemaStore((s) => s.schema);
  const projectName = useSchemaStore((s) => s.projectName);
  const setSelection = useSchemaStore((s) => s.setSelection);
  const storySteps = useMemo(() => parseTourSteps(documentation).length, [documentation]);

  // Clicking a reference in the explanation selects that table, group, enum or field on the canvas (which centres on it)
  // without leaving the About tab, so the reader keeps their place in the text.
  const handleReference: ReferenceHandler = (kind, target) => {
    const { schema } = useSchemaStore.getState();
    const view = useDiagramViewStore.getState();
    const same = (a: string) => a.toLowerCase() === target.toLowerCase();
    const miss = () => view.showToast(`"${target}" is not in this diagram any more`);

    if (kind === 'table' || kind === 'column') {
      const [tableName, columnName] = kind === 'column' ? target.split('.') : [target, undefined];
      const table = schema.tables.find((t) => t.name.toLowerCase() === tableName.toLowerCase());
      if (!table) return miss();
      const column = columnName ? table.columns.find((c) => c.name.toLowerCase() === columnName.toLowerCase()) : undefined;
      if (columnName && !column) return miss();
      useDiagramViewStore.setState({ keepAboutOpen: true });
      setSelection({ type: 'table', id: table.id });
      if (column) view.focusColumn(table.id, column.id);
    } else if (kind === 'group') {
      const group = schema.groups.find((g) => same(g.name));
      if (!group) return miss();
      useDiagramViewStore.setState({ keepAboutOpen: true });
      setSelection({ type: 'group', id: group.id });
    } else if (kind === 'enum') {
      const found = schema.enums.find((e) => same(e.name));
      if (!found) return miss();
      useDiagramViewStore.setState({ keepAboutOpen: true });
      setSelection({ type: 'enum', id: found.id });
    }
  };

  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-2 text-sm">
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Tables</div>
          <div className="font-semibold dark:text-gray-100">{stats.tables.length}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Relationships</div>
          <div className="font-semibold dark:text-gray-100">{stats.relationships.length}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Groups</div>
          <div className="font-semibold dark:text-gray-100">{stats.groups.length}</div>
        </div>
        <div className="rounded-md bg-gray-50 p-2 dark:bg-gray-800">
          <div className="text-xs text-gray-400 dark:text-gray-500">Enums</div>
          <div className="font-semibold dark:text-gray-100">{stats.enums.length}</div>
        </div>
      </div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">About this diagram</div>
      <DiagramExplanation
        value={documentation}
        onChange={setDocumentation}
        title={projectName}
        onReference={handleReference}
        storySteps={storySteps}
        onPlayStory={(start) => useDiagramViewStore.getState().openTour(start)}
        placeholder={'# What this schema is for\n\nExplain the domain, key tables and naming conventions...'}
      />
    </div>
  );
}

type PanelTab = 'properties' | 'documentation' | 'source';

function PropertiesTabContent({ selection }: { selection: SelectionRef }) {
  if (!selection) {
    return (
      <div className="p-3 text-sm text-gray-400 dark:text-gray-500">
        Nothing selected. Click a table, group, relationship, or enum to edit its properties.
      </div>
    );
  }
  return (
    <div className="p-3">
      {selection.type === 'table' && <TableProperties tableId={selection.id} />}
      {selection.type === 'group' && <GroupProperties groupId={selection.id} />}
      {selection.type === 'relationship' && <RelationshipProperties relationshipId={selection.id} />}
      {selection.type === 'enum' && <EnumProperties enumId={selection.id} />}
    </div>
  );
}

export default function PropertiesPanel({ width }: { width: number }) {
  const selection = useSchemaStore((s) => s.selection);
  const [activeTab, setActiveTab] = useState<PanelTab>('documentation');
  const docsRequested = useDiagramViewStore((s) => s.docsRequested);
  const requestDocs = useDiagramViewStore((s) => s.requestDocs);

  // The toolbar's "About" button asks for this tab (also when the panel was closed and has just been opened).
  useEffect(() => {
    if (!docsRequested) return;
    setActiveTab('documentation');
    requestDocs(false);
  }, [docsRequested, requestDocs]);

  useEffect(() => {
    if (!selection) return;
    // A reference clicked in the About tab selects something on the canvas; stay on About so the reader keeps their place.
    if (useDiagramViewStore.getState().keepAboutOpen) {
      useDiagramViewStore.setState({ keepAboutOpen: false });
      return;
    }
    setActiveTab('properties');
  }, [selection]);

  const tabs: { key: PanelTab; label: string }[] = [
    { key: 'documentation', label: 'About' },
    { key: 'properties', label: selection ? selection.type.charAt(0).toUpperCase() + selection.type.slice(1) : 'Properties' },
    { key: 'source', label: 'DBML Source' },
  ];

  return (
    <div
      style={{ width }}
      className="flex h-full shrink-0 flex-col border-l border-gray-200/70 bg-panel dark:border-white/10 dark:bg-gray-900"
    >
      <div className="flex border-b border-gray-200/70 dark:border-white/10">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 truncate px-2 py-2 text-sm font-medium ${
              activeTab === t.key
                ? 'border-b-2 border-sky-600 text-sky-700 dark:border-sky-400 dark:text-sky-300'
                : 'text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'properties' && <PropertiesTabContent selection={selection} />}
        {activeTab === 'documentation' && (
          <div className="p-3">
            <ProjectDocumentation />
          </div>
        )}
        {activeTab === 'source' && (
          <div className="h-full">
            <DbmlEditor />
          </div>
        )}
      </div>
    </div>
  );
}
